import { Typography } from '@mui/material';
import { BackupStatus } from '@repo/api-gw-sdk';
import type { Condition } from '@repo/api-gw-sdk';
import type { SortingState } from '@tanstack/react-table';
import { useState } from 'react';

import { createBackupStatusNotInCondition } from '@/components/backupStatus/createBackupStatusNotInCondition';
import { id } from '@/components/queryBuilder/properties';
import { ColumnSelector } from '@/components/table/columnSelector';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';
import { useDAL } from '@/contexts/useDAL';
import { buildPolicyResourcesInventoryCondition } from '@/types/policyResourcesInventoryCondition';

import type { BackupPolicyEditState } from './BackupPolicyCreationFlow';
import { BackupPolicySupportedDisclaimer } from './customPolicySupportedDisclaimer';
import { useBackupPolicyAvailableTypes } from './useBackupPolicyAvailableTypes';

import { InventoryListTable } from '../../inventory/inventoryListTable';

export const BackupPolicyResourcesList = (
  props: StepProps<BackupPolicyEditState>
) => {
  const baseCondition = useBuildCondition(props);

  return (
    <>
      <StepContainer
        stepperLabels={props.stepperLabels}
        stepperIndex={1}
        onBackClick={props.back}
        canGoNext={() => true}
        onNextClick={async () => {
          props.setNextStep(props.currentStep.next?.[0]);
        }}
        sx={{ padding: 0 }}
        navigationComponent={
          <RenameEntityName
            label='Backup policy name'
            name={props.wizardState.name}
            onSubmit={(name) =>
              props.setWizardState((state) => ({ ...state, name }))
            }
          />
        }
      >
        <BackupPolicySupportedDisclaimer
          policyType={props.wizardState.policyType}
        />
        <Typography className='text-center leading-5 my-[20px]'>
          These are the resources that meet the conditions you set. Review the
          list to make sure it contains the resources you expected.
        </Typography>
        <InventoryTable query={baseCondition} />
      </StepContainer>
    </>
  );
};

const InventoryTable = (props: { query?: Condition }) => {
  const dal = useDAL();
  const [sorting, setSorting] = useState<SortingState>([
    { id: id.name, desc: false },
  ]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  const { data: storageSizeData, error: storageSizeError } =
    dal.inventory.useTotalSourceStorageSize(props.query);

  return (
    <InventoryListTable
      isMultiSelect={false}
      query={props.query}
      showFiltersBar={false}
      sorting={sorting}
      pagination={pagination}
      onPaginationChange={setPagination}
      onSortingChange={(id, desc) => setSorting([{ id, desc }])}
      paginationBarActions={[ColumnSelector]}
      totalSourceStorageSizeBytes={
        storageSizeError
          ? undefined
          : (storageSizeData?.totalSourceStorageSizeBytes ?? 0)
      }
    />
  );
};

const backupStatusNotInCondition = createBackupStatusNotInCondition([
  BackupStatus.ExcludedFromBackup,
  BackupStatus.InitialClassification,
  BackupStatus.Terminated,
  BackupStatus.Disconnected,
  BackupStatus.Unsupported,
  BackupStatus.LimitExceeded,
]);

const useBuildCondition = (props: StepProps<BackupPolicyEditState>) => {
  const availableTypes =
    useBackupPolicyAvailableTypes()[props.wizardState.policyType];
  const resourceTypes = props.wizardState.resourceTypes?.length
    ? props.wizardState.resourceTypes
    : availableTypes;

  return buildPolicyResourcesInventoryCondition(props.wizardState, {
    resourceTypes,
    additionalScopedConditions: [backupStatusNotInCondition],
  });
};
